import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { generateEvidenceHash } from '../../utils/helpers';
import { format } from 'date-fns';

export const EvidenceUpload = ({ milestoneId, onUpload, onClose }) => {
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [metadata, setMetadata] = useState({
    description: '',
    uploadedBy: 'Project Manager',
    gpsLocation: '',
    witnessName: ''
  });

  const acceptedTypes = {
    'image/jpeg': '.jpg, .jpeg',
    'image/png': '.png',
    'application/pdf': '.pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'video/mp4': '.mp4'
  };

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    processFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    processFiles(selectedFiles);
  };

  const processFiles = (fileList) => {
    const validFiles = fileList.filter(file => {
      if (!Object.keys(acceptedTypes).includes(file.type)) {
        alert(`File type not supported: ${file.name}`);
        return false;
      }
      if (file.size > maxFileSize) {
        alert(`File too large (max 10MB): ${file.name}`);
        return false;
      }
      return true;
    });

    const processedFiles = validFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      hash: generateEvidenceHash(),
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      uploadedAt: new Date().toISOString()
    }));

    setFiles(prev => [...prev, ...processedFiles]);
  };

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      alert('Please select at least one file');
      return;
    }

    setUploading(true);
    
    // Simulate upload delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    const uploadedEvidence = files.map(f => ({
      id: f.id,
      name: f.name,
      hash: f.hash,
      type: f.type,
      size: f.size,
      uploadedAt: f.uploadedAt,
      metadata: {
        ...metadata,
        milestoneId
      }
    }));

    onUpload(uploadedEvidence);
    setUploading(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Upload Milestone Evidence</h2>
            <p className="text-sm text-slate-500">Attach photos, documents, or videos as proof of completion</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
          >
            <SafeIcon icon={FiIcons.FiX} className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Drag & Drop Zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={clsx(
              "border-2 border-dashed rounded-lg p-8 text-center transition-all",
              dragActive 
                ? "border-grn-navy bg-blue-50" 
                : "border-slate-300 hover:border-slate-400"
            )}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="h-16 w-16 bg-slate-100 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiUploadCloud} className="text-3xl text-slate-400" />
              </div>
              <div>
                <p className="text-slate-700 font-medium">
                  Drag and drop files here, or{' '}
                  <label className="text-grn-navy font-bold cursor-pointer hover:underline">
                    browse
                    <input
                      type="file"
                      multiple
                      accept={Object.values(acceptedTypes).join(',')}
                      onChange={handleFileInput}
                      className="hidden"
                    />
                  </label>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Supported: Images (JPG, PNG), PDF, DOCX, Video (MP4) • Max 10MB per file
                </p>
              </div>
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                Selected Files ({files.length})
              </h3>
              {files.map(file => (
                <div 
                  key={file.id} 
                  className="flex items-start gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg"
                >
                  {/* Preview */}
                  <div className="h-16 w-16 bg-white border border-slate-200 rounded-lg overflow-hidden flex-shrink-0">
                    {file.preview ? (
                      <img src={file.preview} alt={file.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <SafeIcon 
                          icon={
                            file.type === 'application/pdf' ? FiIcons.FiFileText :
                            file.type.startsWith('video/') ? FiIcons.FiVideo :
                            FiIcons.FiFile
                          }
                          className="text-2xl text-slate-400"
                        />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{file.name}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatFileSize(file.size)} • SHA-256: {file.hash.substring(0, 16)}...
                    </p>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => removeFile(file.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiIcons.FiTrash2} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Metadata Form */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
              Evidence Metadata
            </h3>
            
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Description *
              </label>
              <textarea
                value={metadata.description}
                onChange={(e) => setMetadata({ ...metadata, description: e.target.value })}
                placeholder="Describe what this evidence shows (e.g., 'Foundation inspection completed, all structural tests passed')"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-grn-navy/20"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Uploaded By
                </label>
                <input
                  type="text"
                  value={metadata.uploadedBy}
                  onChange={(e) => setMetadata({ ...metadata, uploadedBy: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-grn-navy/20"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  GPS Location (Optional)
                </label>
                <input
                  type="text"
                  value={metadata.gpsLocation}
                  onChange={(e) => setMetadata({ ...metadata, gpsLocation: e.target.value })}
                  placeholder="-22.5609, 17.0658"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-grn-navy/20"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Witness Name (Optional)
              </label>
              <input
                type="text"
                value={metadata.witnessName}
                onChange={(e) => setMetadata({ ...metadata, witnessName: e.target.value })}
                placeholder="Name of witness or inspector present"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-grn-navy/20"
              />
            </div>
          </div>

          {/* Legal Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="text-amber-600 text-lg flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              <p className="font-bold mb-1">Legal Notice</p>
              <p>
                All uploaded evidence becomes part of the official project record and may be subject to audit. 
                False or misleading evidence may result in legal action under the Public Procurement Act.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex justify-between items-center bg-slate-50">
          <div className="text-xs text-slate-500">
            {files.length} file(s) ready to upload
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={files.length === 0 || !metadata.description || uploading}
              className="px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {uploading ? (
                <>
                  <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiIcons.FiUpload} />
                  Upload Evidence
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};